import React, { useState, useEffect, useRef } from "react";
import { graphql } from "gatsby";
import styled, { css } from "styled-components";
import { Parallax } from "react-scroll-parallax";
import Img from "gatsby-image";
import {
  Row,
  Col,
  Gap,
  TextNice as Text,
  Title,
  TitleNice,
  Section,
  Button,
  TechnologyTop,
  TechnologyTopVideo,
  Link,
  SEO,
} from "../components";
import { up, addResponsivity } from "../lib/styles";
import RotorAnimation from "../components/RotorAnimation";

const CockpitWrapper = styled.div`
  position: relative;
  width: 100%;
`;
// const SkyWrapper = styled.div`
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   overflow: hidden;
//   ${addResponsivity()}
// `

const Subtitle = TitleNice.Subtitle;

const StyledSkyWrapper = styled.div`
  ${({ theme: { colors }, height }) => css`
    position: relative;
    width: 100%;
    overflow: hidden;
    background: white;

    height: ${height}px;
  `}
`;

const SkyWrapper = ({ zuriSkyImage }) => {
  const isBrowser = typeof window !== "undefined";
  const [windowWidth, setWindowWidth] = useState(
    isBrowser ? window.innerWidth : 0,
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const imageWidth = windowWidth > 1920 ? 1920 : windowWidth;
  const wrapperHeight = imageWidth * 0.42;

  return (
    <StyledSkyWrapper height={wrapperHeight}>
      <Parallax y={[-30, 0]}>
        <Img
          fluid={zuriSkyImage.image.fluid}
          alt="Image of Zuri Hybrid VTOL in forward flight above the clouds, representing its redundant fly-by-wire control system, AI-assisted autopilot, and onboard computer vision for enhanced safety and situational awareness."
        />
      </Parallax>
    </StyledSkyWrapper>
  );
};

const HighlightImageCol = (props) => (
  <Col
    grow="1"
    maxWidth="740px"
    mobileMaxWidth="600px"
    bp="tablet"
    shrink="1"
    basis="50%"
    {...props}
  />
);
const HighlightTextCol = (props) => (
  <Col maxWidth="500px" shrink="3" {...props} />
);

// const InlineHeader = (props) => (
//   <Text
//     as="h2"
//     black
//     style={{ textTransform: 'uppercase' }}
//     {...props}
//   />
// )

const InlineHeader = TitleNice.InlineHeader;

class Index extends React.Component {
  rotorRef = React.createRef();

  render() {
    const {
      location: { pathname },
      data: {
        t1,
        t2,
        t3,
        // cockpitImage,
        // skyImage,
        // cabinImage1,
        // cabinImage2,
        zuriSkyImage,
      } = {},
    } = this.props;

    return (
      <>
        <SEO
          title="VTOL Technology & Safety Systems | Zuri"
          description="Discover Zuri’s advanced VTOL technology with tiltrotors, flight control systems, and AI-powered safety, designed for efficient air mobility."
          pathname={pathname}
        />
        <Gap.Top />
        <div style={{ width: "100%", background: "white" }}>
          <TechnologyTopVideo />
          <Section background="white">
            <Gap gap="60px" mobileGap="60px" />
            <Title.Big innerAs="h1" uppercase>
              Zuri Hybrid VTOL Technology
            </Title.Big>
            <Gap gap="150px" mobileGap="50px" bp="tablet" />
            <Row
              responsive
              bp="tablet"
              alignItems="center"
              mobileAlignItems="unset"
            >
              <HighlightImageCol maxHeight="500px">
                <Img
                  ref={this.rotorRef}
                  fluid={t1.image.fluid}
                  imgStyle={{
                    objectFit: "contain",
                  }}
                  alt="Image showing the tiltrotor mechanism on a hybrid VTOL aircraft, demonstrating the transition from vertical takeoff to forward cruise using electric propulsion."
                />
                <RotorAnimation rotorImageRef={this.rotorRef} />
              </HighlightImageCol>

              <Gap gap="80px" mobileGap="30px" bp="tablet" />
              <HighlightTextCol>
                <Subtitle>Tiltrotor System & Electric Propulsion. </Subtitle>
                <Gap gap="16px" />
                <Text noMargin>
                  Features four propulsion units at the front and four at the
                  back, capable of tilting between vertical and&nbsp;horizontal
                  flight.
                </Text>
              </HighlightTextCol>
            </Row>

            <Gap gap="140px" mobileGap="80px" showBelow="desktop" bp="tablet" />
            <Gap gap="120px" showAfter="desktop" />

            <Row
              responsive
              bp="tablet"
              alignItems="center"
              mobileAlignItems="unset"
            >
              <HighlightImageCol maxHeight="360px">
                <Img
                  fluid={t2.image.fluid}
                  imgStyle={{
                    objectFit: "contain",
                  }}
                  alt="Image showing the onboard turbine generator system of a hybrid VTOL aircraft, which supplies electric energy for propulsion and extends flight range beyond conventional eVTOLs."
                />
              </HighlightImageCol>
              <Gap gap="80px" mobileGap="40px" bp="tablet" />
              <HighlightTextCol>
                <Subtitle>Hybrid Power Generation & Energy Supply. </Subtitle>
                <Gap gap="16px" />
                <Text noMargin>
                  Onboard turbine generator supplies electric energy, enabling a
                  longer range than eVTOLs.
                </Text>
              </HighlightTextCol>
            </Row>

            <Gap gap="140px" mobileGap="80px" showBelow="desktop" bp="tablet" />
            <Gap gap="120px" showAfter="desktop" />

            <Row
              responsive
              bp="tablet"
              alignItems="center"
              mobileAlignItems="unset"
            >
              <HighlightImageCol maxHeight="457px">
                <Img
                  fluid={t3.image.fluid}
                  imgStyle={{
                    objectFit: "contain",
                  }}
                  alt="Image showing the integrated battery packs of a Hybrid VTOL aircraft, featuring lightweight lithium cells, fireproof insulation, and an intelligent battery management system for safety and efficiency."
                />
              </HighlightImageCol>
              <Gap gap="80px" mobileGap="40px" bp="tablet" />
              <HighlightTextCol>
                <Subtitle>Battery Pack & Safety Systems. </Subtitle>
                <Gap gap="16px" />
                <Text noMargin>
                  Lightweight lithium cells with an intelligent battery
                  management system and fireproof insulation.
                </Text>
              </HighlightTextCol>
            </Row>
            <Gap gap="180px" mobileGap="80px" />
          </Section>
        </div>

        <SkyWrapper zuriSkyImage={zuriSkyImage} />

        <Section>
          <Gap gap="125px" mobileGap="40px" bp="tablet" />
          <Row justifyContent="center" responsive bp="tablet">
            <Col maxWidth="500px">
              <Subtitle>Flight Control & Autopilot Technology. </Subtitle>
              <Gap gap="16px" />
              <Text noMargin>
                Redundant fly-by-wire system and AI-driven autopilot for safer
                flight.
              </Text>
            </Col>
            <Gap gap="80px" mobileGap="40px" bp="tablet" />
            <Col maxWidth="500px">
              <Subtitle>AI Sensors & Computer Vision. </Subtitle>
              <Gap gap="16px" />
              <Text noMargin>
                Computer vision enhances situational awareness for pilots with
                object recognition and 3D modeling.
              </Text>
            </Col>
          </Row>
          <Gap gap="125px" mobileGap="40px" bp="tablet" />
          <Row justifyContent="center">
            <Button.Link to={Link.STORY}>STORY BEHIND</Button.Link>
          </Row>
          <Gap gap="80px" mobileGap="40px" />
        </Section>
      </>
    );
  }
}

export default Index;

export const pageQuery = graphql`
  fragment TechnologyImage1 on File {
    image: childImageSharp {
      fluid(maxWidth: 675, quality: 80) {
        # fluid(maxWidth: 1280, maxHeight: 618, quality: 95) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
  fragment TechnologyImage2 on File {
    image: childImageSharp {
      fluid(maxWidth: 620, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
  fragment TechnologyImage3 on File {
    image: childImageSharp {
      fluid(maxWidth: 620, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }

  fragment TopMobileImage on File {
    image: childImageSharp {
      fluid(maxWidth: 980, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }

  query {
    t1: file(
      relativePath: {
        eq: "images/technology/tiltrotor-transition-electric-propulsion-hybrid-vtol.png"
      }
    ) {
      ...TechnologyImage1
    }
    t2: file(
      relativePath: {
        eq: "images/technology/hybrid-vtol-onboard-turbine-electric-power-generation-system.png"
      }
    ) {
      ...TechnologyImage2
    }
    t3: file(
      relativePath: {
        eq: "images/technology/hybrid-vtol-battery-pack-safety-system-lithium-bms-insulation.png"
      }
    ) {
      ...TechnologyImage3
    }
    zuriSkyImage: file(
      relativePath: {
        eq: "images/technology/zuri-hybrid-vtol-autopilot-ai-sensor-computer-vision-fly-by-wire.jpg"
      }
    ) {
      ...FullHdImage
    }
    # cabinImage1: file(
    #   relativePath: { eq: "images/technology/zuri-cabin-1.jpg" }
    # ) {
    #   ...FullHdImage
    # }
    # cabinImage2: file(relativePath: { eq: "images/technology/zuri.jpg" }) {
    #   ...FullHdImage
    # }
    # cockpitImage: file(relativePath: { eq: "images/technology/cockpit.png" }) {
    #   ...FullHdImage
    # }
    # skyImage: file(relativePath: { eq: "images/technology/sky.jpg" }) {
    #   ...FullHdImage
    # }
  }
`;
